"use client";

import { ctaClick } from "@/customAnalytics/trackingEvents";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import Image from "next/image";
import Link from "next/link";

/**
 * About 1028 — BDU About Us.
 *
 * Port of BDU v1 `components/Home/AboutUs.tsx`: square image on one side, title with
 * a gradient rule under it, justified body copy and a gradient-text "Know More" link.
 *
 * BREAKPOINTS — v1 `md:` (768px) is `tab:` here; v1 `lg:` (1024px) has no token in this
 * app's scale, so it is written as the arbitrary variant `min-[1024px]:`.
 *
 * The reference reformats the incoming title with a regex that splits camelCase and
 * title-cases each word ("AboutUs" -> "About Us"). That is reproduced so CMS values
 * authored the old way render identically.
 */

interface About1028Data {
  title?: string;
  title_text_color?: string;
  description?: string;
  description_text_color?: string;
  desktop_image?: string;
  alt_image?: string;
  /** Gradient rule under the title */
  gradient_color_1?: string;
  gradient_color_2?: string;
  /** "Know More" gradient text */
  gradient_color_3?: string;
  gradient_color_4?: string;
  read_more_text?: string;
  cta_link?: string;
  is_cta?: boolean;
  status?: boolean;
}

/** "AboutUs" -> "About Us"; already-spaced titles pass through unchanged. */
function formatTitle(raw: string): string {
  return raw
    .replace(/([A-Z])/g, " $1")
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();
}

const About1028 = ({ data }: { data: any }) => {
  const sectionData = (data?.data ?? data) as About1028Data | undefined;

  const {
    title = "",
    title_text_color = "#222023",
    description = "",
    description_text_color = "#4F4F4F",
    desktop_image = "",
    alt_image = "Thumbnail",
    gradient_color_1 = "#1F3D56",
    gradient_color_2 = "#7CB5E6",
    gradient_color_3 = "#FF0013",
    gradient_color_4 = "#990013",
    read_more_text = "Know More",
    cta_link = "/about-us",
  } = sectionData || {};

  const showCta = isCmsShowFlagOn(sectionData?.is_cta, true);

  if (!sectionData?.status) return null;

  return (
    <div className="size-full">
      <div className="w-full px-7 pb-[48px] pt-[34px] tab:px-16 tab:pb-[61px] tab:pt-[42px]">
        <div className="flex flex-col-reverse items-center justify-around gap-[16px] tab:gap-[88px] min-[1024px]:flex-row">
          <div className="flex items-center justify-start">
            <div className="flex h-[292px] w-[320px] cursor-pointer items-center justify-center tab:size-[363px] tab:rounded-[20px]">
              <Image
                src={desktop_image || "/images/brokenImage.webp"}
                alt={alt_image || "Thumbnail"}
                className="size-full rounded-[20px] object-cover"
                width={363}
                height={363}
                priority
              />
            </div>
          </div>

          <div className="flex w-full flex-col items-start justify-around gap-y-3">
            <div className="flex flex-col gap-2">
              <p
                className="text-2xl font-bold tab:text-[28px]"
                style={{ color: title_text_color }}
              >
                {formatTitle(title)}
              </p>
              <div
                className="h-[3px] w-[94px] tab:w-[148px]"
                style={{
                  backgroundImage: `linear-gradient(90deg, ${gradient_color_1} 0%, ${gradient_color_2} 100%)`,
                }}
              />
            </div>

            <p
              className="w-fit whitespace-pre-wrap text-justify text-[12px]/[23px] font-light leading-7 tab:text-[14px]/[28px]"
              style={{ color: description_text_color }}
            >
              {description}
            </p>

            {showCta && read_more_text && (
              <Link
                href={cta_link || "#"}
                className="cursor-pointer bg-clip-text text-sm/[28px] font-semibold text-transparent"
                style={{
                  backgroundImage: `linear-gradient(to right, ${gradient_color_3}, ${gradient_color_4})`,
                  // Lightning CSS emits no -webkit- prefix; without this the
                  // gradient-clipped label is invisible in WebKit.
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                }}
                onClick={() => {
                  ctaClick({
                    section: "about_1028",
                    component: "read_more",
                    label: read_more_text,
                    step: "",
                    entity: "about_1028",
                    parentEntity: "about_1028",
                  });
                }}
              >
                {read_more_text}
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default About1028;
